import { datadogRum } from "@datadog/browser-rum";
import {
  formatState,
  getCityFromAddressComponents,
  getLatLngAsValue,
  getStreetInfoFromAddressComponents,
  parseAddressComponents,
} from "./mapsLocationHelper";
import { upperCaseFirstWord } from "./titleCase";
import { DestinationListItemType } from "../types";

type FormattedAddress = {
  addressLine1: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  state: string;
  zipCode: string;
  addressLine2?: string;
  bizName?: string;
  dealerId?: string;
  error?: { errorMessage: string };
  locationType?: string;
};

type FormatAddressParams = {
  addressInfo: any;
  isTowToDestinationAddress: boolean;
  address?: any;
  isPepPREnabled?: boolean;
  partnerCode?: string;
  towToCustomDestinationList?: any[];
  towToLocationType?: any;

};

const createReadableErrorMessageForMissingFields = (fieldErrors): string => {
  let readableErrors = "";
  for (let int = 0; fieldErrors && int < fieldErrors.length; int++) {
    if (fieldErrors.length === 1) {
      readableErrors = readableErrors.concat(`${upperCaseFirstWord(String(fieldErrors[int] ?? ""))} is missing.`);
    } else if (int === fieldErrors.length - 1) {
      readableErrors = readableErrors.concat(`and ${fieldErrors[int].toString()} are missing.`);
    } else if (int === 0) {
      readableErrors = readableErrors.concat(`${upperCaseFirstWord(fieldErrors[int].toString())}, `);
    } else readableErrors = readableErrors.concat(`${fieldErrors[int].toString()}, `);
  }
  return `Invalid Address: ${readableErrors}`;
};

export const formatAddress = ({
  address,
  addressInfo,
  isPepPREnabled = false,
  isTowToDestinationAddress = true,
  partnerCode,
  towToCustomDestinationList,
}: FormatAddressParams) => {
  try {
    const formattedAddress: FormattedAddress = {
      addressLine1: "",
      city: "",
      country: "",
      latitude: 0,
      longitude: 0,
      state: "",
      zipCode: "",
      locationType: ""
    };
    const addressDetails = parseAddressComponents(addressInfo?.address_components);

    let fieldErrors = [];

    formattedAddress.addressLine1 = getStreetInfoFromAddressComponents(addressDetails);
    if (addressDetails["neighborhood,political"])
      formattedAddress.addressLine2 = addressDetails["neighborhood,political"];
    formattedAddress.city = getCityFromAddressComponents(addressDetails);
    if (isPepPREnabled) {
      // B-2567882 PR as state code
      formattedAddress.state = addressDetails["country,political"];
    } else {
      formattedAddress.state = formatState(addressDetails["administrative_area_level_1,political"]);
    }
    formattedAddress.zipCode = addressDetails["postal_code"];
    formattedAddress.country = addressDetails["country,political"];

    const { lat, lng } = getLatLngAsValue(addressInfo?.geometry?.location);
    formattedAddress.latitude = lat;
    formattedAddress.longitude = lng;
    let requiredFields = ["city", "state", "zipCode", "country", "latitude", "longitude", "addressLine1"];
    let selectedDestinationByBusinessName: DestinationListItemType;
    if (towToCustomDestinationList?.length > 0) {
      selectedDestinationByBusinessName = getCorrectAddress({towToCustomDestinationList, address});
      formattedAddress.dealerId = selectedDestinationByBusinessName?.dealerId;
      formattedAddress.bizName = selectedDestinationByBusinessName?.name;
      formattedAddress.locationType = selectedDestinationByBusinessName?.destinationType;
    }

    if (towToCustomDestinationList?.length > 0 && isTowToDestinationAddress && selectedDestinationByBusinessName) {
      requiredFields = requiredFields.concat(["dealerId", "bizName"]);
    }

    requiredFields.forEach((field) => {
      if (!formattedAddress[field] || String(formattedAddress[field] ?? "").length === 0) {
        if (field === "zipCode") fieldErrors.push("zip code");
        else if (field === "bizName" && isTowToDestinationAddress) fieldErrors.push("business name");
        else if (field === "dealerId" && isTowToDestinationAddress) fieldErrors.push("Dealer Id");
        else fieldErrors.push(field);
      }
    });

    if (fieldErrors.length) {
      if (addressInfo.hasOwnProperty("utc_offset")) delete addressInfo.utc_offset;
      datadogRum.addAction("Missing address fields for address", { fieldErrors, addressInfo });
      let errorMessage = createReadableErrorMessageForMissingFields(fieldErrors);
      formattedAddress.error = { errorMessage: errorMessage };
    }
    return formattedAddress;
  } catch (error) {
    datadogRum?.addError(new Error(`formatAddress error ${error}`));
    return { error: { errorMessage: `Could not populate all required fields ${error}`, missingFields: [] } };
  }
};

export const getCorrectAddress = ({towToCustomDestinationList, address}) =>{
  return towToCustomDestinationList.find((destination: DestinationListItemType) => {
    const destinationName = destination.searchableName?.replace(/\s/g, "");
    const addressWithoutSpace = address?.replace(/\s/g, "");
    return destinationName?.toUpperCase() === addressWithoutSpace?.toUpperCase();
  });
}
